// frontend-crm/src/components/WhatsAppPanel.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  QrCode as QrCodeIcon,
  Chat as ChatIcon,
  CheckCircle as OnlineIcon,
  Error as OfflineIcon
} from '@mui/icons-material';
import { useQuery, useMutation } from 'react-query';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function WhatsAppPanel() {
  const [selectedChat, setSelectedChat] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  const [broadcastNumbers, setBroadcastNumbers] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');

  // Estado del bot
  const { data: botStatus, refetch: refetchStatus } = useQuery(
    'botStatus',
    () => fetch(`${API_URL}/api/baileys/status`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
    { refetchInterval: 5000 }
  );

  // Chats recientes (desde backend)
  const { data: chats } = useQuery(
    'chats',
    () => fetch(`${API_URL}/api/admin/chats`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
    { refetchInterval: 3000 }
  );

  // Enviar mensaje individual
  const sendMessage = useMutation(
    (data) => fetch(`${API_URL}/api/baileys/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(data)
    }).then(r => r.json()),
    {
      onSuccess: () => {
        setMessageText('');
        // Refrescar chats
      }
    }
  );

  // Broadcast
  const sendBroadcast = useMutation(
    (data) => fetch(`${API_URL}/api/baileys/broadcast`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(data)
    }).then(r => r.json())
  );

  const handleReconnect = async () => {
    try {
      await fetch(`${API_URL}/api/baileys/reconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      refetchStatus();
    } catch (err) {
      console.error("Error reconectando:", err);
    }
  };

  const handleLogout = async () => {
    if (window.confirm("¿Estás seguro de que deseas cerrar sesión? Esto eliminará las credenciales de WhatsApp.")) {
      try {
        await fetch(`${API_URL}/api/baileys/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        refetchStatus();
      } catch (err) {
        console.error("Error al cerrar sesión:", err);
      }
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', gap: 2 }}>
      {/* Sidebar - Estado y Controles */}
      <Paper sx={{ width: 300, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" gutterBottom>
          WhatsApp Bot
        </Typography>

        {/* Estado de Conexión */}
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {botStatus?.state === 'connected' ? (
                <OnlineIcon color="success" />
              ) : (
                <OfflineIcon color="error" />
              )}
              <Typography variant="subtitle1">
                {botStatus?.state === 'connected' ? 'Conectado' : 'Desconectado'}
              </Typography>
            </Box>
            
            {botStatus?.state !== 'connected' && botStatus?.qr && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Se requiere escanear QR
              </Alert>
            )}
            
            <Typography variant="caption" color="textSecondary">
              Cola: {botStatus?.queue_size || 0} mensajes
            </Typography>
          </CardContent>
        </Card>

        {/* Controles */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<QrCodeIcon />}
            onClick={() => {
              refetchStatus();
              setQrDialogOpen(true);
            }}
            disabled={botStatus?.state === 'connected'}
            sx={{ flex: '1 1 100%' }}
          >
            Ver QR
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReconnect}
            sx={{ flex: 1 }}
          >
            Reiniciar
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleLogout}
            sx={{ flex: 1 }}
          >
            Salir
          </Button>
        </Box>

        <Button
          variant="contained"
          onClick={() => setBroadcastDialogOpen(true)}
          fullWidth
        >
          Mensaje Masivo
        </Button>

        {/* Lista de Chats */}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Conversaciones Recientes
        </Typography>
        <List dense sx={{ overflow: 'auto', flex: 1 }}>
          {chats?.data?.map((chat) => (
            <ListItem
              key={chat.telefono}
              button
              selected={selectedChat?.telefono === chat.telefono}
              onClick={() => setSelectedChat(chat)}
            >
              <ListItemAvatar>
                <Avatar>
                  <ChatIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={chat.nombre || chat.telefono}
                secondary={
                  <Typography noWrap variant="body2">
                    {chat.ultimo_mensaje?.substring(0, 30)}...
                  </Typography>
                }
              />
              {chat.no_leidos > 0 && (
                <Chip size="small" color="primary" label={chat.no_leidos} />
              )}
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Área de Chat */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {selectedChat ? (
          <>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                {selectedChat.nombre || selectedChat.telefono}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedChat.telefono}
              </Typography>
            </Box>

            {/* Mensajes */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'grey.50' }}>
              {selectedChat.mensajes?.map((msg, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: msg.direccion === 'saliente' ? 'flex-end' : 'flex-start',
                    mb: 1
                  }}
                >
                  <Paper
                    sx={{
                      p: 1.5,
                      maxWidth: '70%',
                      bgcolor: msg.direccion === 'saliente' ? 'primary.main' : 'background.paper',
                      color: msg.direccion === 'saliente' ? 'primary.contrastText' : 'text.primary'
                    }}
                  >
                    <Typography variant="body2">{msg.mensaje}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.5 }}>
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>

            {/* Input */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                maxRows={3}
                placeholder="Escribe un mensaje..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage.mutate({
                      telefono: selectedChat.telefono,
                      mensaje: messageText,
                      delay_ms: 2000 // Simular humano
                    });
                  }
                }}
              />
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={() => sendMessage.mutate({
                  telefono: selectedChat.telefono,
                  mensaje: messageText,
                  delay_ms: 2000
                })}
                disabled={!messageText.trim() || sendMessage.isLoading}
              >
                Enviar
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography color="textSecondary">
              Selecciona una conversación
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Dialog QR */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Escanea el QR con WhatsApp</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', p: 3 }}>
          {botStatus?.qr ? (
            <Box
              component="img"
              src={botStatus.qr}
              alt="QR Code"
              sx={{ width: 300, height: 300, bgcolor: '#fff', p: 2, borderRadius: 2 }}
            />
          ) : (
            <CircularProgress />
          )}
          <Typography variant="body2" sx={{ mt: 2 }}>
            Abre WhatsApp en tu teléfono → Menú → Dispositivos vinculados → Vincular
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Broadcast */}
      <Dialog 
        open={broadcastDialogOpen} 
        onClose={() => setBroadcastDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Mensaje Masivo</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Números (uno por línea)"
                placeholder="3001234567\n3009876543\n..."
                value={broadcastNumbers}
                onChange={(e) => setBroadcastNumbers(e.target.value)}
                helperText="Máximo 50 números por hora para evitar bloqueos"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Mensaje"
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                Maximo 1000 caracteres por mensaje.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBroadcastDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => {
              const numeros = broadcastNumbers.split('\n').filter(n => n.trim());
              sendBroadcast.mutate({
                telefonos: numeros,
                mensaje: broadcastMessage,
                intervalo_min: 5000,
                intervalo_max: 15000
              });
              setBroadcastDialogOpen(false);
            }}
            disabled={!broadcastNumbers.trim() || !broadcastMessage.trim()}
          >
            Enviar a {broadcastNumbers.split('\n').filter(n => n.trim()).length} números
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}